import { Edit, UserX } from "lucide-react";
import type { DataTableColumn } from "../../../../components/table";
import PopoverDateRangeFilterComponent from "../../../../components/customs/table-header/popover-date-range-filter";
import PopoverRoleFilterComponent from "../../../../components/customs/table-header/popover-role-filter";
import PopoverUserStatusFilterComponent from "../../../../components/customs/table-header/popover-user-status-filter";
import type { UserFilters, UserItem } from "../../types/user.types";
import StablePopoverAgeOrDateOfBirthFilterComponent from "./StablePopoverAgeOrDateOfBirthFilterComponent";
import _ from "lodash";

export interface UserListTableLabels {
  name: string;
  email: string;
  age: string;
  joinDate: string;
  role: string;
  status: string;
  lastActive: string;
  actions: string;
}

export const getUserStatusColor = (status: string): string => {
  switch (status) {
    case "active":
      return "bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-300";
    case "inactive":
      return "bg-gray-100 text-gray-800 dark:bg-gray-900 dark:text-gray-300";
    case "pending":
      return "bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-300";
    default:
      return "bg-gray-100 text-gray-800 dark:bg-gray-900 dark:text-gray-300";
  }
};

export const getUserRoleColor = (role: string): string => {
  switch (role) {
    case "super_admin":
      return "bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-300";
    case "admin":
      return "bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-300";
    case "developer":
      return "bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-300";
    case "user":
      return "bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-300";
    default:
      return "bg-gray-100 text-gray-800 dark:bg-gray-900 dark:text-gray-300";
  }
};

interface BuildUserListColumnsArgs {
  filters: UserFilters;
  onFiltersChange: React.Dispatch<React.SetStateAction<UserFilters>>;
  labels: UserListTableLabels;
  roleLabels: Record<string, string>;
  getStatusColor?: (status: string) => string;
  getRoleColor?: (role: string) => string;
  onDeactivate: (user: UserItem) => void;
  navigateToUpdate: (user: UserItem) => void;
}

export const buildUserListColumns = ({
  filters,
  onFiltersChange,
  labels,
  roleLabels,
  getStatusColor = getUserStatusColor,
  getRoleColor = getUserRoleColor,
  onDeactivate,
  navigateToUpdate,
}: BuildUserListColumnsArgs): DataTableColumn<UserItem>[] => {
  const filterState = { filter: filters, setFilter: onFiltersChange };

  return [
    {
      key: "name",
      title: labels.name,
      filter: {
        type: "string",
        value: filters.name,
        onFilter: (value) => onFiltersChange((current) => ({ ...current, name: value })),
        placeholder: labels.name,
        title: labels.name,
        submitMode: "change",
      },
      render: (user) => `${user.first_name} ${user.last_name}`,
    },
    {
      key: "email",
      title: labels.email,
      filter: {
        type: "string",
        value: filters.email,
        onFilter: (value) => onFiltersChange((current) => ({ ...current, email: value })),
        placeholder: labels.email,
        title: labels.email,
        submitMode: "change",
      },
      render: (user) => user.email,
    },
    {
      key: "age",
      title: labels.age,
      filter: (
        <StablePopoverAgeOrDateOfBirthFilterComponent
          filterState={{ ...filterState, filterKey: "age" }}
          title={labels.age}
        />
      ),
      cell: {
        type: "age",
        ageKey: "age",
        dateOfBirthKey: "date_of_birth",
      },
    },
    {
      key: "join_date",
      title: labels.joinDate,
      filter: (
        <PopoverDateRangeFilterComponent
          filterState={{ ...filterState, filterKey: "join_date" }}
          title={labels.joinDate}
          isOpen={false}
          onOpenChange={() => undefined}
          datePickerVersion="2"
        />
      ),
      render: (user) =>
        new Date(user.created_at).toLocaleDateString("en-US", {
          month: "2-digit",
          day: "2-digit",
          year: "numeric",
        }),
    },
    {
      key: "role",
      title: labels.role,
      filter: (
        <PopoverRoleFilterComponent
          filterState={{ ...filterState, filterKey: "role" }}
          title={labels.role}
          isOpen={false}
          onOpenChange={() => undefined}
        />
      ),
      render: (user) => (
        <span className={`rounded px-2 py-1 text-xs ${getRoleColor(user.role)}`}>
          {roleLabels[user.role] || user.role}
        </span>
      ),
    },
    {
      key: "status",
      title: labels.status,
      filter: (
        <PopoverUserStatusFilterComponent
          filterState={{ ...filterState, filterKey: "status" }}
          title={labels.status}
          isOpen={false}
          onOpenChange={() => undefined}
        />
      ),
      render: (user) => {
        if (_.isNil(user?.status)) {
          return <span>unknow</span>;
        }
        return (
          <span className={`rounded px-2 py-1 text-xs ${getStatusColor(user.status)}`}>
            {user.status.charAt(0).toUpperCase() + user.status.slice(1)}
          </span>
        );
      },
    },
    {
      key: "last_active_at",
      title: labels.lastActive,
      cell: {
        type: "last_active",
        valueKey: "last_active_at",
      },
    },
    {
      key: "current_point",
      title: "Current Point",
      render: (user) => user.current_point ?? 0,
    },
    {
      key: "actions",
      title: labels.actions,
      render: (user) => (
        <div className="flex gap-2">
          <button
            type="button"
            aria-label="Edit user"
            className="rounded border p-2"
            onClick={(event) => {
              event.stopPropagation();
              navigateToUpdate(user);
            }}
          >
            <Edit className="h-4 w-4" />
          </button>
          <button
            type="button"
            aria-label="Deactivate user"
            className="rounded border p-2 text-red-600"
            onClick={(event) => {
              event.stopPropagation();
              onDeactivate(user);
            }}
          >
            <UserX className="h-4 w-4" />
          </button>
        </div>
      ),
    },
  ];
};
